import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { Box, Button, CardActionArea, Modal, Skeleton } from '@mui/material';
import { FullProperty } from '../../models/fullProperty';
import "./index.css"
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import AsyncCardMedia from '../../shared/asyncCardMedia';
import AsyncString from '../../shared/asyncString';
import { Coordinates } from '../dialog/mapDialog';
import { SaleItem } from '../../models/saleItem';
import { TimeServices } from '../../services/functions/timeServices';
import { useEffect, useState } from 'react';
import { SingleTimePill } from '../../shared/closingTimePill';
import { RestaurantDiscountPill } from '../../shared/restaurantDiscountPill';
import { DistancePill } from '../../shared/distancePill';
import { RestaurantHelper } from '../../services/functions/restaurantHelper';
import { SupriseBagsRemaining } from '../../shared/surpriseBagsRemainingPill';
import { StarRating } from './rating/StarRatingComponent';
import { CommentsClient } from '../../services/functions/commentsClient';
import dayjs from 'dayjs';

interface IProps {
    listing: FullProperty
    onCardClick: (listing: FullProperty) => void;
}

interface CardProps {
    listing: FullProperty;
    onCardClick: (listing: FullProperty) => void;
}

const LOCAL_STORAGE_KEY = "localLocation";

const getStoredCoordinates = (): Coordinates | null => {
    const storedLocation = localStorage.getItem(LOCAL_STORAGE_KEY);

    if (storedLocation) {
        const parsedLocation = JSON.parse(storedLocation);

        // Ensure it matches the Coordinates structure
        return {
            lat: parsedLocation.lat,
            lng: parsedLocation.lng,
        };
    }

    return null;
};

function showListedItems(listing: FullProperty) {
    console.log("listing.saleItems", listing.id, listing.saleItems)
    console.log("listing.saleItems", listing.id, listing.saleItems.filter((s) => s.isActive).length > 0)
    return listing.saleItems.filter((s) => s.isActive).length > 0;
}

function MobileCard({
    listing,
    onCardClick
}: CardProps) {
    const [nameLoaded, setNameLoaded] = useState(false);
    const [summaryLoaded, setSummaryLoaded] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [rating, setRating] = useState<number>(0);
    const [numRatings, setNumRatings] = useState<number>(0);

    useEffect(() => {
        const fetchRating = async () => {
            const { comments: _comments, rating: _rating, numRatings: _numRatings } = await CommentsClient.getComments(listing.id)
            setRating(_rating);
            setNumRatings(_numRatings);
        }
        fetchRating()
    })
    const [closingTime, setClosingTime] = useState<string>();
    // Get the current moment.
    const now = new Date();

    const setCurrentClosingTime = () => {
        const operatingDay = TimeServices.getOperatingDayType(now);
        const closingTime = TimeServices.getCurrentEndDateTime(operatingDay, listing);
        setClosingTime(closingTime);
    }


    useEffect(() => {
        setCurrentClosingTime();
    }, [listing]);

    const isClosed = ({ saleItems }: { saleItems: SaleItem[] }): boolean => {
        const totalQuantity = RestaurantHelper.getTotalQuantity(saleItems);
        if (totalQuantity == null) return true;
        return TimeServices.isClosed(listing) && totalQuantity <= 0;
    };


    return (
        <div
            className="ListingCardBase"
            onClick={() => onCardClick(listing)}
            style={{ cursor: "pointer" }}
        >
            <CardActionArea>
                <Card
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        maxWidth: 400, // narrower
                        height: 280,   // shorter than before
                        borderRadius: 3,
                        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                        overflow: "hidden",
                        opacity: listing.isComingSoon ||  isClosed({ saleItems: listing.saleItems }) ? 0.4 : 1,
                        position: "relative",
                        margin: "0 auto"
                    }}
                >
                    {/* Status Overlay */}
                    {listing.isComingSoon ? (
                        <Box
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontSize: "20px",
                                fontWeight: "bold",
                                color: "black",
                                zIndex: 10,
                                backgroundColor: "rgba(255,255,255,0.7)",
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                            }}
                        >
                            COMING SOON
                        </Box>
                    ) : isClosed({ saleItems: listing.saleItems }) && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                fontSize: "20px",
                                fontWeight: "bold",
                                color: "black",
                                zIndex: 10,
                                backgroundColor: "rgba(255,255,255,0.7)",
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                            }}
                        >
                            OFFLINE
                        </Box>
                    )}

                    {/* Image on top */}
                    <Box sx={{ position: "relative", width: "100%", height: 140 }}>
                        {!imageLoaded && <Skeleton variant="rectangular" width="100%" height="100%" />}
                        <AsyncCardMedia
                            component="img"
                            imageId={listing.id + "-thumbnail"}
                            alt="Listing thumbnail"
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "opacity 0.3s ease-in-out",
                                opacity: listing.isComingSoon || isClosed({ saleItems: listing.saleItems }) ? 0.3 : 1,
                            }}
                            onLoad={() => setImageLoaded(true)}
                        />
                        {/* Overlay logo */}
                        <img
                            src="/src_assets/pbt_logo.png"
                            alt="Overlay icon"
                            style={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                border: "2px solid white",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                            }}
                        />
                    </Box>
                    {/* Content under image */}
                    <CardContent
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            flex: 1,
                            p: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="h6" component="div">
                                <AsyncString
                                    stringId={listing.id + "-restaurant-name"}
                                    onLoad={() => setNameLoaded(true)}
                                    style={{
                                        visibility: nameLoaded ? "visible" : "hidden",
                                    }}
                                />
                                {!nameLoaded && <Skeleton variant="text" width="60%" />}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                <AsyncString
                                    stringId={listing.id + "-short-restaurant-summary"}
                                    onLoad={() => setSummaryLoaded(true)}
                                    style={{
                                        visibility: summaryLoaded ? "visible" : "hidden",
                                    }}
                                />
                                {!summaryLoaded && <Skeleton variant="text" width="80%" />}
                            </Typography>
                        </Box>
                        {showListedItems(listing) && (
                            <SupriseBagsRemaining saleItems={listing.saleItems} />
                        )}
                        <Box display="flex" flexDirection="row" alignItems="center" gap={1.5}>
                            <StarRating value={rating} numRatings={numRatings} />
                            <Box
                                sx={{
                                    width: "1px",
                                    height: 16,
                                    bgcolor: "#000",
                                    mx: 0.01,
                                }}
                            />
                            <DistancePill
                                itemLat={listing.location.lat}
                                itemLon={listing.location.lng}
                                userLat={getStoredCoordinates()?.lat ?? 0.0}
                                userLon={getStoredCoordinates()?.lng ?? 0.0}
                            />
                            <Box
                                sx={{
                                    width: "1px",
                                    height: 16,
                                    bgcolor: "#000",
                                    mx: 0.01,
                                }}
                            />
                            <SingleTimePill time={new Date(listing.closingTime)} />
                        </Box>
                    </CardContent>
                </Card>
            </CardActionArea>
        </div>
    );
}


function ListingCard({
    listing,
    onCardClick,
}: IProps) {
    return <MobileCard
        listing={listing}
        onCardClick={onCardClick}
    />
}

export { ListingCard };
